import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../hooks/useAuth.tsx';
import { http } from '../lib/http';
import { useDispatchMode } from '../hooks/useDispatchMode';
import { Logout as LogoutIcon } from '@mui/icons-material';
import { UnitType } from '../types/index.ts';

interface ShiftSelectionProps {
  onShiftSelected: (shiftData: {
    callsign: string;
    role: string;
    unitId?: string;
    unitRole?: string;
  }) => void;
}

const ShiftSelection: React.FC<ShiftSelectionProps> = ({ onShiftSelected }) => {
  const [callsign, setCallsign] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedUnitRole, setSelectedUnitRole] = useState('');
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [availableUnits, setAvailableUnits] = useState<any[]>([]);
  const [unitClaims, setUnitClaims] = useState<any>({});
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [userAssignment, setUserAssignment] = useState<any>(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const { user, logout } = useAuth();
  const { isActiveDispatchMode, isLoading: modeLoading } = useDispatchMode();

  // Load unit types from database
  const loadUnitTypes = async () => {
    try {
      const response = await http.get('/unit-assignments/unit-types');
      if (response.data.success) {
        const types = response.data.data || [];
        setUnitTypes(types);
        if (types.length === 0) {
          console.warn('[ShiftSelection] No unit types returned from server, will use fallback roles');
        } else {
          console.log('[ShiftSelection] Loaded', types.length, 'unit types from server');
        }
      } else {
        console.warn('[ShiftSelection] Server returned error for unit types:', response.data.error, '- will use fallback roles');
      }
    } catch (error: any) {
      // Don't show error to user - fallback roles will be used instead
      if (error.response?.status === 403) {
        console.warn('[ShiftSelection] Access denied to unit types endpoint (403), will use fallback roles');
      } else {
        console.warn('[ShiftSelection] Failed to load unit types:', error.message || error, '- will use fallback roles');
      }
      // Keep unitTypes empty so getAvailableUnitRoles uses fallback
    }
  };

  // Fallback unit roles based on unit type prefix (used when API fails)
  // Matches the database schema from unit_types table
  const getFallbackUnitRoles = (unitType: string): string[] => {
    if (!unitType) return [];
    
    const typeUpper = unitType.toUpperCase();
    
    // ENG, TRK, TNKR, RESQ, DIV: required ["ENG", "OFFC"], optional ["FF1", "FF2", "FF3", "FF4"]
    if (typeUpper === 'ENG' || typeUpper === 'TRK' || typeUpper === 'TNKR' || typeUpper === 'RESQ' || typeUpper === 'DIV') {
      return ['ENG', 'OFFC', 'FF1', 'FF2', 'FF3', 'FF4'];
    }
    
    // BRSH: required ["ENG", "OFFC"], optional ["FF1", "FF2", "FF3"]
    if (typeUpper === 'BRSH') {
      return ['ENG', 'OFFC', 'FF1', 'FF2', 'FF3'];
    }
    
    // SUPV: required ["OFFC"], optional ["DRVR"]
    if (typeUpper === 'SUPV') {
      return ['OFFC', 'DRVR'];
    }
    
    // ALS, BLS: required ["DRVR", "ATTD"], optional ["3RD", "4TH"]
    if (typeUpper === 'ALS' || typeUpper === 'BLS') {
      return ['DRVR', 'ATTD', '3RD', '4TH'];
    }
    
    // HELI: required ["Pilot 1"], optional ["Pilot 2"]
    if (typeUpper === 'HELI') {
      return ['Pilot 1', 'Pilot 2'];
    }
    
    // OTHER: required ["POS1"], optional ["POS2", "POS3", "POS4", "POS5"]
    if (typeUpper === 'OTHER') {
      return ['POS1', 'POS2', 'POS3', 'POS4', 'POS5'];
    }
    
    // Default fallback for unknown types (use ENG pattern)
    return ['ENG', 'OFFC', 'FF1', 'FF2', 'FF3', 'FF4'];
  };

  // Get available unit roles from database based on unit type
  const getAvailableUnitRoles = (unitType: string): string[] => {
    if (!unitType) {
      return [];
    }
    
    // If unit types loaded successfully, use database data
    if (unitTypes.length > 0) {
      const unitTypeData = unitTypes.find(ut => ut.id === unitType?.toUpperCase());
      if (unitTypeData) {
        // Combine required and optional roles
        return [...unitTypeData.requiredRoles, ...unitTypeData.optionalRoles];
      }
    }
    
    // Fallback to hardcoded roles if database lookup fails or unit types not loaded
    console.warn('[ShiftSelection] Using fallback unit roles for type:', unitType);
    return getFallbackUnitRoles(unitType);
  };

  useEffect(() => {
    // Only load data if user is authenticated
    if (!user) {
      console.log('[ShiftSelection] User not authenticated yet, skipping data load');
      return;
    }
    
    loadUnitTypes();
    loadAvailableRoles();
    loadAvailableUnits();
    loadUnitClaims();
  }, [user]);

  // Check for user assignment in Active Dispatch mode
  useEffect(() => {
    if (isActiveDispatchMode && user) {
      checkUserAssignment();
    }
  }, [isActiveDispatchMode, user]);

  // Refresh unit claims when user assignment changes
  useEffect(() => {
    if (userAssignment?.unitId) {
      loadUnitClaims();
    }
  }, [userAssignment]);

  // Auto-select first available role when unit claims are loaded and no role is selected
  useEffect(() => {
    if ((selectedRole === 'unit' || selectedRole === 'command') && selectedUnit && !selectedUnitRole) {
      const finalUnitId = userAssignment?.unitId || selectedUnit;
      const selectedUnitObj = getFilteredUnits().find(unit => unit.id === finalUnitId);
      
      if (selectedUnitObj && Object.keys(unitClaims).length > 0) {
        const availableRoles = getAvailableUnitRoles(selectedUnitObj.type);
        const unitClaimers = unitClaims[finalUnitId] || [];
        
        // Find first available role (prefer Engineer/Driver, then Officer, then others)
        // Use database role names: Engineer, Driver, Officer, Firefighter 1, etc.
        const preferredRoles = ['Engineer', 'Driver', 'Officer', 'Firefighter 1', 'Paramedic', 'EMT 1', 'Technician 1', 'Commander'];
        const availableRole = preferredRoles.find(role => {
          if (!availableRoles.includes(role)) return false;
          const isRoleTaken = Array.isArray(unitClaimers) && 
            unitClaimers.some((claimer) => 
              claimer.role === role && claimer.id !== user?.id
            );
          return !isRoleTaken;
        });
        
        // If no preferred role available, use first available role
        if (!availableRole) {
          const firstAvailable = availableRoles.find(role => {
            const isRoleTaken = Array.isArray(unitClaimers) && 
              unitClaimers.some((claimer) => 
                claimer.role === role && claimer.id !== user?.id
              );
            return !isRoleTaken;
          });
          if (firstAvailable) {
            setSelectedUnitRole(firstAvailable);
            setError('');
            return;
          }
        }
        
        if (availableRole) {
          setSelectedUnitRole(availableRole);
          setError(''); // Clear any errors
        }
      }
    }
  }, [unitClaims, selectedUnit, selectedRole, selectedUnitRole, userAssignment, user]);

  const checkUserAssignment = async () => {
    setAssignmentLoading(true);
    try {
      console.log('[ShiftSelection] Checking user assignment for user:', user?.id);
      const response = await http.get('/unit-assignments/big-board');
      console.log('[ShiftSelection] Big board response:', response.data);
      
      if (response.data.success) {
        const assignments = response.data.data.assignments || {};
        console.log('[ShiftSelection] Assignments data:', assignments);
        
        // assignments is an object grouped by unit, so we need to search through all units
        let myAssignment = null;
        for (const unitId in assignments) {
          const unitAssignments = assignments[unitId];
          if (Array.isArray(unitAssignments)) {
            const found = unitAssignments.find((a: any) => a.userId === user?.id);
            if (found) {
              myAssignment = found;
              break;
            }
          }
        }
        
        console.log('[ShiftSelection] Found assignment:', myAssignment);
        if (myAssignment) {
          setUserAssignment(myAssignment);
          setSelectedUnit(myAssignment.unitId);
        } else {
          setUserAssignment(null);
        }
      }
    } catch (error) {
      console.error('Error checking user assignment:', error);
      setUserAssignment(null);
    } finally {
      setAssignmentLoading(false);
    }
  };

  const loadAvailableRoles = async () => {
    try {
      const response = await http.get('/auth/available-roles');
      console.log('Available roles response:', response.data);
      if (response.data.success) {
        // Filter out 'admin' and 'command' roles since they're now accessed via buttons in interfaces
        const filteredRoles = response.data.data.availableRoles.filter((role: string) => role !== 'admin' && role !== 'command');
        
        // Fallback: if all roles were filtered out, check if user has a valid role
        if (filteredRoles.length === 0) {
          const currentRole = response.data.data.currentRole || user?.role;
          // If user only has admin/command, they can't use shift selection
          if (currentRole && currentRole !== 'admin' && currentRole !== 'command') {
            console.warn('[ShiftSelection] All roles filtered out, falling back to current role:', currentRole);
            setAvailableRoles([currentRole]);
            setSelectedRole(currentRole);
          } else {
            console.error('[ShiftSelection] User has no available roles for shift selection');
            setError('You do not have any available roles for shift selection. Please contact an administrator.');
            setAvailableRoles([]);
          }
        } else {
          setAvailableRoles(filteredRoles);
          console.log('Set available roles:', filteredRoles);
          // Set default role if user only has one option
          if (filteredRoles.length === 1) {
            setSelectedRole(filteredRoles[0]);
          }
        }
      } else {
        // Server returned success: false
        console.error('[ShiftSelection] Server returned error:', response.data.error);
        const fallbackRole = user?.role;
        if (fallbackRole && fallbackRole !== 'admin' && fallbackRole !== 'command') {
          console.warn('[ShiftSelection] Falling back to user role:', fallbackRole);
          setAvailableRoles([fallbackRole]);
          setSelectedRole(fallbackRole);
        } else {
          setError(response.data.error || 'Failed to load available roles. Please contact an administrator.');
          setAvailableRoles([]);
        }
      }
    } catch (error: any) {
      console.error('Error loading available roles:', error);
      // Network or other error - try fallback to user's current role
      const fallbackRole = user?.role;
      if (fallbackRole && fallbackRole !== 'admin' && fallbackRole !== 'command') {
        console.warn('[ShiftSelection] Network error, falling back to user role:', fallbackRole);
        setAvailableRoles([fallbackRole]);
        setSelectedRole(fallbackRole);
      } else {
        setError('Failed to load available roles. Please refresh the page or contact an administrator.');
        setAvailableRoles([]);
      }
    }
  };

  const loadAvailableUnits = async () => {
    try {
      const response = await http.get('/units');
      if (response.data.success) {
        setAvailableUnits(response.data.data);
      }
    } catch (error) {
      console.error('Error loading available units:', error);
    }
  };

  const loadUnitClaims = async () => {
    try {
      const response = await http.get('/units/claims/all');
      if (response.data?.success) {
        console.log('[ShiftSelection] Loaded unit claims:', response.data.data);
        setUnitClaims(response.data.data);
      }
    } catch (error) {
      console.error('[ShiftSelection] Failed to load unit claims:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!callsign.trim()) {
      setError('Callsign is required');
      setLoading(false);
      return;
    }

    if (!selectedRole) {
      setError('Please select a role');
      setLoading(false);
      return;
    }

    // In Active Dispatch mode for unit/command roles, user must have an assignment
    if (isActiveDispatchMode && (selectedRole === 'unit' || selectedRole === 'command')) {
      if (assignmentLoading) {
        setError('Please wait while we check your assignment...');
        setLoading(false);
        return;
      }
      if (!userAssignment) {
        setError('You have not been assigned to a unit. Please contact a dispatcher or shift commander.');
        setLoading(false);
        return;
      }
      // Use assigned unit
      setSelectedUnit(userAssignment.unitId);
    }

    // In Self Dispatch mode, require unit selection for unit and command roles
    if (!isActiveDispatchMode && (selectedRole === 'unit' || selectedRole === 'command') && !selectedUnit) {
      setError('Please select a unit');
      setLoading(false);
      return;
    }

    // Require unit role selection for unit and command roles
    if ((selectedRole === 'unit' || selectedRole === 'command') && !selectedUnitRole) {
      setError('Please select a unit role');
      setLoading(false);
      return;
    }

    // Check if the selected unit role is already taken by another user
    if ((selectedRole === 'unit' || selectedRole === 'command') && selectedUnitRole) {
      const finalUnitId = userAssignment?.unitId || selectedUnit;
      const unitClaimers = unitClaims[finalUnitId];
      
      if (Array.isArray(unitClaimers)) {
        const roleTaken = unitClaimers.some((claimer) => 
          claimer.role === selectedUnitRole && claimer.id !== user?.id
        );
        
        if (roleTaken) {
          setError(`Role ${selectedUnitRole} is already taken by another user in unit ${finalUnitId}`);
          setLoading(false);
          return;
        }
      }
    }

    try {
      // For unit and command roles, attempt to claim the unit during shift selection
      if (selectedRole === 'unit' || selectedRole === 'command') {
        const finalUnitId = userAssignment?.unitId || selectedUnit;
        
        try {
          // Attempt to claim the unit with the selected role
          const claimResponse = await http.post('/auth/claim-unit', {
            unitId: finalUnitId,
            role: selectedUnitRole
          });
          
          if (!claimResponse.data.success) {
            setError(claimResponse.data.error || 'Failed to claim unit');
            setLoading(false);
            return;
          }
          
          console.log('[ShiftSelection] Successfully claimed unit:', finalUnitId, 'with role:', selectedUnitRole);
          
          // Update localStorage to prevent duplicate claims in UnitInterface
          localStorage.setItem('claimed_unit_id', finalUnitId);
          localStorage.setItem('claimed_unit_role', selectedUnitRole);
        } catch (claimError: any) {
          const errorMsg = claimError.response?.data?.error || 'Failed to claim unit';
          setError(errorMsg);
          setLoading(false);
          return;
        }
      }
      
      // If we get here, unit claiming was successful (or not needed)
      onShiftSelected({
        callsign: callsign.trim(),
        role: selectedRole,
        unitId: (selectedRole === 'unit' || selectedRole === 'command') 
          ? (userAssignment?.unitId || selectedUnit) 
          : undefined,
        unitRole: (selectedRole === 'unit' || selectedRole === 'command') 
          ? selectedUnitRole 
          : undefined
      });
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to start shift. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredUnits = () => {
    // For unit and command roles, show all units except DISPATCH
    const filtered = availableUnits.filter(unit => unit.id !== 'DISPATCH');
    
    // Sort by type order as defined in the system
    filtered.sort((a, b) => {
      const typeOrder = {
        'ENG': 1, 'TRK': 2, 'ALS': 3, 'RESQ': 4, 'SUPV': 5, 'BLS': 6,
        'TNKR': 7, 'BRSH': 8, 'DIV': 9, 'HELI': 10, 'OTHER': 11, 'DISP': 12
      };
      
      const aTypeOrder = typeOrder[a.type as keyof typeof typeOrder] || 999;
      const bTypeOrder = typeOrder[b.type as keyof typeof typeOrder] || 999;
      
      if (aTypeOrder !== bTypeOrder) {
        return aTypeOrder - bTypeOrder;
      }
      
      // If same type, sort by unit ID
      return a.id.localeCompare(b.id);
    });
    
    return filtered;
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 500 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{ color: '#3c8cff', fontWeight: 'bold' }}
              >
                Shift Selection
              </Typography>
              <Button
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={() => { void logout(); }}
                sx={{
                  color: '#ff6b6b',
                  borderColor: '#ff6b6b',
                  '&:hover': {
                    borderColor: '#ff5252',
                    backgroundColor: 'rgba(255, 107, 107, 0.1)',
                  },
                }}
              >
                Logout
              </Button>
            </Box>
            <Typography
              variant="subtitle1"
              align="center"
              gutterBottom
              sx={{ color: '#b4b9c0', mb: 3 }}
            >
              Welcome, {user?.robloxName}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Callsign"
                    value={callsign}
                    onChange={(e) => setCallsign(e.target.value)}
                    required
                    autoFocus
                    placeholder="Enter your callsign"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={selectedRole}
                      onChange={(e) => {
                        setSelectedRole(e.target.value);
                        setSelectedUnit(''); // Reset unit selection when role changes
                        setSelectedUnitRole(''); // Reset unit role selection when role changes
                      }}
                      label="Role"
                      disabled={availableRoles.length === 0}
                    >
                      {availableRoles.length === 0 ? (
                        <MenuItem disabled value="">
                          <em>No roles available</em>
                        </MenuItem>
                      ) : (
                        availableRoles.map((role) => (
                          <MenuItem key={role} value={role}>
                            {role.charAt(0).toUpperCase() + role.slice(1)}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Unit selection - conditional based on dispatch mode */}
                {(selectedRole === 'unit' || selectedRole === 'command') && (
                  <Grid item xs={12}>
                    {modeLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : isActiveDispatchMode ? (
                      // Active Dispatch Mode: Show message instead of dropdown
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          Active Dispatch Mode
                        </Typography>
                        <Typography variant="body2">
                          {assignmentLoading ? (
                            <>Checking your assignment...</>
                          ) : userAssignment ? (
                            <>You have been assigned to unit <strong>{userAssignment.unitId}</strong>. 
                            You will need to claim this unit after logging in.</>
                          ) : (
                            <>You have not been assigned to a unit for this shift. 
                            Please contact a dispatcher or shift commander for unit assignment.</>
                          )}
                        </Typography>
                      </Alert>
                    ) : (
                      // Self Dispatch Mode: Show unit dropdown
                      <Autocomplete
                        value={getFilteredUnits().find(unit => unit.id === selectedUnit) || null}
                        onChange={(_, newValue) => {
                          setSelectedUnit(newValue?.id || '');
                          setSelectedUnitRole(''); // Reset unit role when unit changes
                          loadUnitClaims(); // Refresh unit claims when unit changes
                        }}
                        options={getFilteredUnits()}
                        getOptionLabel={(option) => `${option.id} - ${option.type} (${option.status})`}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Unit"
                            required
                            placeholder="Search for a unit..."
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {option.id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.type} • {option.status}
                              </Typography>
                            </Box>
                          </li>
                        )}
                      />
                    )}
                  </Grid>
                )}

                {/* Unit Role selection - only show for unit and command roles */}
                {(selectedRole === 'unit' || selectedRole === 'command') && selectedUnit && (
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Unit Role</InputLabel>
                      <Select
                        value={selectedUnitRole}
                        onChange={(e) => {
                          const newRole = e.target.value;
                          const finalUnitId = userAssignment?.unitId || selectedUnit;
                          const unitClaimers = unitClaims[finalUnitId] || [];
                          
                          // Check if the role is taken by another user
                          const isRoleTaken = Array.isArray(unitClaimers) && 
                            unitClaimers.some((claimer) => 
                              claimer.role === newRole && claimer.id !== user?.id
                            );
                          
                          if (isRoleTaken) {
                            setError(`Role ${newRole} is already taken by another user in unit ${finalUnitId}`);
                            return; // Don't set the role if it's taken
                          } else {
                            setError(''); // Clear any previous errors
                            setSelectedUnitRole(newRole);
                          }
                        }}
                        label="Unit Role"
                      >
                        {(() => {
                          const finalUnitId = userAssignment?.unitId || selectedUnit;
                          const selectedUnitObj = getFilteredUnits().find(unit => unit.id === finalUnitId);
                          const availableUnitRoles = selectedUnitObj ? getAvailableUnitRoles(selectedUnitObj.type) : [];
                          const unitClaimers = unitClaims[finalUnitId] || [];
                          
                          return availableUnitRoles.map((unitRole) => {
                            const isRoleTaken = Array.isArray(unitClaimers) && 
                              unitClaimers.some((claimer) => 
                                claimer.role === unitRole && claimer.id !== user?.id
                              );
                            
                            return (
                              <MenuItem 
                                key={unitRole} 
                                value={unitRole}
                                disabled={isRoleTaken}
                                sx={{
                                  color: isRoleTaken ? '#ff6b35' : 'inherit',
                                  fontStyle: isRoleTaken ? 'italic' : 'normal'
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                  <Typography sx={{ fontSize: 14, fontWeight: 'bold' }}>
                                    {unitRole}
                                  </Typography>
                                  {isRoleTaken && (
                                    <Typography sx={{ fontSize: 12, color: '#ff6b35', fontStyle: 'italic' }}>
                                      (Taken)
                                    </Typography>
                                  )}
                                </Box>
                              </MenuItem>
                            );
                          });
                        })()}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || assignmentLoading}
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    backgroundColor: '#3c8cff',
                    '&:hover': {
                      backgroundColor: '#2a7ae9',
                    },
                  }}
                >
                  {loading ? 'Starting Shift...' : assignmentLoading ? 'Checking Assignment...' : 'Start Shift'}
                </Button>
            </form>

            <Box sx={{ mt: 3, p: 2, backgroundColor: '#16181d', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: '#8c9198', mb: 1 }}>
                Role Information:
              </Typography>
              <Typography variant="body2" sx={{ color: '#b4b9c0', fontSize: '0.8rem' }}>
                • Admin: Full system access and user management (accessed via admin button)
              </Typography>
              <Typography variant="body2" sx={{ color: '#b4b9c0', fontSize: '0.8rem' }}>
                • Dispatcher: Call management and unit coordination (no unit required)
              </Typography>
              <Typography variant="body2" sx={{ color: '#b4b9c0', fontSize: '0.8rem' }}>
                • Command: Field operations with command access (accessed via shift commander button)
              </Typography>
              <Typography variant="body2" sx={{ color: '#b4b9c0', fontSize: '0.8rem' }}>
                • Unit: Field operations and call response 
                {isActiveDispatchMode ? ' (assignment by dispatcher required)' : ' (unit selection required)'}
              </Typography>
              
              {!modeLoading && (
                <Typography variant="body2" sx={{ color: '#ffa726', fontSize: '0.75rem', mt: 1.5, fontStyle: 'italic' }}>
                  Mode: {isActiveDispatchMode ? 'Active Dispatch' : 'Self Dispatch'}
                  {isActiveDispatchMode && ' - Units must be assigned by dispatcher/commander'}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ShiftSelection;
